import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import get from 'lodash.get';
import { connect } from 'react-redux';

import {
  AccountPurchasedCustomPrints,
  PurchasedCustomPrintForConfig,
} from '../../graphql/queries/custom-prints';
import {
  CUSTOM_PRINT_TYPE,
  ORDER_STATUS,
} from '../../../../constants/pro-custom-prints';
import CustomPrintConfiguratorManager from '../../components/custom-prints/CustomPrintConfiguratorManager';
import { purchasedCustomPrintUpdate } from '../../../../graphql/mutations/custom-prints';
import { openModal } from '../../../../actions/modal-actions';
import { CustomPrintConfigurationPhotos } from '../../../../graphql/queries/custom-prints';
import { createCustomPrintCandidatePhotoGroups } from '../../../../components/global/CustomPrintConfigurator/helpers';

const propTypes = {
  loading: PropTypes.bool,
  albumCustomPrint: PropTypes.object,
  candidatePhotoGroups: PropTypes.array,
  loaders: PropTypes.shape({
    loadMoreMatchedPhotos: PropTypes.func,
  }),
  pendingPurchasedCustomPrints: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  customPrintUpdate: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
};

const CustomPrintConfiguratorContainer = ({ ...props }) => {
  return (
    <CustomPrintConfiguratorManager {...props} path="/consumer/custom-prints" />
  );
};

CustomPrintConfiguratorContainer.propTypes = propTypes;

const withPurchasedCustomPrint = graphql(PurchasedCustomPrintForConfig, {
  options: ({ match }) => ({
    variables: {
      id: match.params.purchasedCustomPrintId,
    },
  }),
  props: ({ data: { purchasedCustomPrint, loading } }) => ({
    loading,
    albumCustomPrint: purchasedCustomPrint,
  }),
});

const withPurchasedCustomPrintCandidatePhotos = graphql(
  CustomPrintConfigurationPhotos,
  {
    options: ({ match }) => ({
      variables: {
        purchasedCustomPrintId: match.params.purchasedCustomPrintId,
      },
    }),
    props: ({ data: { customPrintConfigurationPhotos, fetchMore } }) => {
      return {
        candidatePhotoGroups: createCustomPrintCandidatePhotoGroups({
          source: customPrintConfigurationPhotos,
          mappings: [
            {
              id: 'matches',
              label: 'Matches',
              path: 'matched_photos_extended',
              mapper: record => record.photo,
            },
            {
              id: 'favorites',
              label: 'Favorites',
              path: 'favorited_photos',
            },
            {
              id: 'allPhotos',
              label: 'All Photos',
              path: 'all_photos',
            },
          ],
          fetchMore,
        }),
      };
    },
  },
);

const withCustomerInputPurchasedCustomPrints = graphql(
  AccountPurchasedCustomPrints,
  {
    options: () => ({
      variables: {
        orderStatuses: [ORDER_STATUS.CUSTOMER_INPUT],
        customPrintTypes: [
          CUSTOM_PRINT_TYPE.CUSTOM_PRINT,
          CUSTOM_PRINT_TYPE.COLLAGE,
        ],
      },
      fetchPolicy: 'cache-and-network',
    }),
    props: ({ data: { me } }) => ({
      pendingPurchasedCustomPrints: get(
        me,
        'purchased_custom_prints.records',
        [],
      ),
    }),
  },
);

const withPurchasedCustomPrintUpdateMutation = graphql(
  purchasedCustomPrintUpdate,
  {
    props: ({ mutate }) => ({
      customPrintUpdate: ({
        id,
        caption,
        photos,
        placeOrder = true,
        address,
      }) => {
        const addressId = get(address, 'id');

        return mutate({
          variables: {
            input: {
              purchased_custom_print_id: id,
              custom_print_caption: caption,
              photos,
              place_order: placeOrder,
              account_address_id: addressId,
              account_address: addressId ? undefined : address,
            },
          },
          update: store => {
            const data = store.readQuery({
              query: AccountPurchasedCustomPrints,
              variables: {
                orderStatuses: [ORDER_STATUS.CUSTOMER_INPUT],
                customPrintTypes: [
                  CUSTOM_PRINT_TYPE.CUSTOM_PRINT,
                  CUSTOM_PRINT_TYPE.COLLAGE,
                ],
              },
            });

            const purchasedCustomPrints = get(
              data,
              'me.purchased_custom_prints.records',
              [],
            );

            store.writeQuery({
              query: AccountPurchasedCustomPrints,
              variables: {
                orderStatuses: [ORDER_STATUS.CUSTOMER_INPUT],
                customPrintTypes: [
                  CUSTOM_PRINT_TYPE.CUSTOM_PRINT,
                  CUSTOM_PRINT_TYPE.COLLAGE,
                ],
              },
              data: {
                ...data,
                me: {
                  ...data.me,
                  purchased_custom_prints: {
                    ...data.me.purchased_custom_prints,
                    total: data.me.purchased_custom_prints.total - 1,
                    records: purchasedCustomPrints.filter(x => x.id !== id),
                  },
                },
              },
            });
          },
        });
      },
    }),
  },
);

const mapDispatchToProps = {
  openModal,
};

export default compose(
  withRouter,
  withPurchasedCustomPrint,
  withPurchasedCustomPrintCandidatePhotos,
  withPurchasedCustomPrintUpdateMutation,
  withCustomerInputPurchasedCustomPrints,
  connect(null, mapDispatchToProps),
)(CustomPrintConfiguratorContainer);
